import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Search, Calendar as CalIcon, Phone, Mail, MapPin, User, Star, Check } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { GOOGLE_REVIEW_URL } from "../../data/content";

const STATUS_META = {
  outstanding: { label: "Outstanding", bg: "#FF9500", fg: "#000" },
  scheduled:   { label: "Scheduled",   bg: "#0A84FF", fg: "#fff" },
  in_progress: { label: "In Progress", bg: "#FFCC00", fg: "#000" },
  completed:   { label: "Completed",   bg: "#34C759", fg: "#000" },
  invoiced:    { label: "Invoiced",    bg: "#71717A", fg: "#fff" },
};

const BUCKETS = [
  { id: "upcoming",    label: "Upcoming",    hint: "Scheduled or in-progress, in the future" },
  { id: "outstanding", label: "Outstanding", hint: "From new bookings, awaiting scheduling" },
  { id: "completed",   label: "Completed",   hint: "Wrapped up or invoiced" },
];

export default function JobLog() {
  const [bucket, setBucket] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({ upcoming: 0, outstanding: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get(`/admin/jobs`, {
        params: { bucket, ...(search ? { search } : {}) },
      });
      setJobs(r.data || []);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Failed to load jobs.");
    } finally {
      setLoading(false);
    }
  }, [bucket, search]);

  // Load counts for the bucket tabs
  const loadCounts = useCallback(async () => {
    try {
      const results = await Promise.all(
        BUCKETS.map((b) => api.get(`/admin/jobs`, { params: { bucket: b.id } }))
      );
      setCounts({
        upcoming: results[0].data?.length || 0,
        outstanding: results[1].data?.length || 0,
        completed: results[2].data?.length || 0,
      });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts, jobs.length]);

  const setStatus = async (job, newStatus) => {
    try {
      const r = await api.patch(`/admin/jobs/${job.id}`, { status: newStatus });
      setJobs((prev) => prev.map((j) => (j.id === job.id ? r.data : j)));
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Status update failed");
    }
  };

  const grouped = useMemo(() => {
    // For "upcoming" we group by date label; for others, just one flat list.
    if (bucket !== "upcoming") return [["", jobs]];
    const groups = {};
    for (const j of jobs) {
      const d = j.scheduled_at ? new Date(j.scheduled_at) : null;
      const key = d ? d.toLocaleDateString("en-AU", { weekday: "long", day: "2-digit", month: "short", year: "numeric" }) : "Not yet scheduled";
      if (!groups[key]) groups[key] = [];
      groups[key].push(j);
    }
    return Object.entries(groups);
  }, [jobs, bucket]);

  return (
    <div className="space-y-4" data-testid="admin-job-log">
      <div className="flex flex-wrap items-center gap-2">
        {BUCKETS.map((b) => (
          <button
            key={b.id}
            onClick={() => setBucket(b.id)}
            data-testid={`joblog-bucket-${b.id}`}
            title={b.hint}
            className="px-3 py-1.5 text-xs te-overline border"
            style={{
              borderColor: bucket === b.id ? "var(--te-primary)" : "var(--te-border)",
              color: bucket === b.id ? "var(--te-text)" : "var(--te-text-muted)",
            }}
          >
            {b.label} ({counts[b.id]})
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 te-card px-3" style={{ height: "32px" }}>
          <Search size={13} className="text-zinc-500" />
          <input
            data-testid="joblog-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search service, suburb, customer…"
            className="bg-transparent text-sm outline-none w-56"
          />
        </div>
      </div>

      {err && <div className="te-card p-3 text-sm" style={{ color: "var(--te-primary)" }}>{err}</div>}

      {loading ? (
        <div className="te-card p-8 flex items-center justify-center"><Loader2 className="animate-spin" /></div>
      ) : jobs.length === 0 ? (
        <div className="te-card p-12 text-center text-zinc-400">No jobs in this bucket.</div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([label, list]) => (
            <div key={label || "all"}>
              {label && (
                <div className="te-overline text-[10px] mb-2 flex items-center gap-2">
                  <CalIcon size={11} /> {label}
                </div>
              )}
              <div className="space-y-2">
                {list.map((j) => (
                  <JobRow key={j.id} job={j} onStatus={setStatus} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function JobRow({ job, onStatus }) {
  const meta = STATUS_META[job.status] || STATUS_META.outstanding;
  const [copied, setCopied] = useState(false);
  const isDone = job.status === "completed" || job.status === "invoiced";
  const copyReviewSms = async () => {
    const first = (job.customer_name || "").split(" ")[0] || "there";
    const text = `Hi ${first}, thanks for choosing Tech Emergency! If you were happy with the ${job.service || "visit"}, a quick Google review would mean the world to our small Adelaide team: ${GOOGLE_REVIEW_URL} — Thanks! Tech Emergency 1800 836 390`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <div className="te-card p-4 md:p-5 grid md:grid-cols-12 gap-3 items-start" data-testid={`joblog-row-${job.id}`}>
      <div className="md:col-span-1 flex md:flex-col items-center md:items-start gap-2">
        <div
          className="px-2 py-0.5 text-[10px] te-overline"
          style={{ background: meta.bg, color: meta.fg }}
        >
          {meta.label}
        </div>
        {job.scheduled_at && (
          <div className="text-[11px] text-zinc-500">
            {new Date(job.scheduled_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
          </div>
        )}
      </div>
      <div className="md:col-span-5">
        <div className="font-semibold tracking-tight">{job.service}</div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-zinc-400">
          <span className="inline-flex items-center gap-1"><User size={11} /> {job.customer_name || "—"}</span>
          {job.customer_phone && (
            <a href={`tel:${job.customer_phone}`} className="inline-flex items-center gap-1 hover:text-white">
              <Phone size={11} /> {job.customer_phone}
            </a>
          )}
          {job.customer_email && (
            <a href={`mailto:${job.customer_email}`} className="inline-flex items-center gap-1 hover:text-white">
              <Mail size={11} /> {job.customer_email}
            </a>
          )}
          {job.suburb && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {job.suburb}</span>}
        </div>
        {job.internal_notes && (
          <p className="text-xs text-zinc-500 mt-2 line-clamp-2 italic">{job.internal_notes}</p>
        )}
      </div>
      <div className="md:col-span-3 text-xs text-zinc-400">
        {typeof job.cost_total === "number" && (
          <div>
            <div className="te-overline text-[10px]">Total</div>
            <div className="text-base font-bold text-white">${job.cost_total.toFixed(2)}</div>
          </div>
        )}
        {job.customer_notes && (
          <div className="mt-2 line-clamp-2">{job.customer_notes}</div>
        )}
      </div>
      <div className="md:col-span-3 flex flex-wrap gap-1 justify-end items-start">
        {isDone && (
          <button
            onClick={copyReviewSms}
            data-testid={`joblog-${job.id}-copy-review-sms`}
            className="px-2 py-0.5 text-[10px] te-overline inline-flex items-center gap-1 border"
            style={{ borderColor: "var(--te-primary)", color: copied ? "#34C759" : "var(--te-primary)" }}
            title="Copy a ready-to-send review request SMS"
          >
            {copied ? <Check size={10} /> : <Star size={10} />} {copied ? "Copied!" : "Copy review SMS"}
          </button>
        )}
        {job.review_request_sent_at && (
          <span
            className="px-2 py-0.5 text-[10px] te-overline inline-flex items-center gap-1"
            style={{ background: "#1c1c1e", color: "#34C759" }}
            data-testid={`joblog-${job.id}-review-sent`}
            title={`Review request emailed ${new Date(job.review_request_sent_at).toLocaleDateString("en-AU")}`}
          >
            <Star size={10} /> Review requested
          </span>
        )}
        {Object.keys(STATUS_META).map((s) => {
          if (s === job.status) return null;
          const m = STATUS_META[s];
          return (
            <button
              key={s}
              onClick={() => onStatus(job, s)}
              data-testid={`joblog-${job.id}-set-${s}`}
              className="px-2 py-0.5 text-[10px] te-overline opacity-60 hover:opacity-100"
              style={{ background: m.bg, color: m.fg }}
              title={`Mark as ${m.label}`}
            >
              → {m.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
